import React from 'react';
import { Link, NavLink } from 'react-router';

import { BrandLoader } from 'ui/BrandLoader/BrandLoader';
import { useDebouncedClick } from 'ui/hooks';

import clearClasses from './ClearButton.module.scss';
import filledClasses from './FilledButton.module.scss';
import outlinedClasses from './OutlinedButton.module.scss';
import type { ButtonProps } from './types';
import { useButtonView } from './useButtonView';

const rootClassesMap = {
  filled: filledClasses,
  outlined: outlinedClasses,
  clear: clearClasses,
};

const ButtonInternal: React.ForwardRefRenderFunction<HTMLButtonElement, ButtonProps> = (
  props,
  ref,
) => {
  const {
    variant = 'filled',
    shape,
    color,
    size,
    invertPalette,
    focused,
    flexFullSize,
    widthFullSize,
    isIcon,
    textAlign,
    debounceClickTime,
    onClick,
    onDoubleClick,
    children,
    disabled = false,
    loading = false,
    startAdornment,
    endAdornment,
    to,
    nav,
    className,
    style,
    ...domProps
  } = props;

  const { rootProps, labelProps, startAdornmentProps, endAdornmentProps } =
    useButtonView(props, rootClassesMap[variant]);

  const handleClick = useDebouncedClick(onClick, debounceClickTime);

  const content = (
    <>
      {startAdornment && (
        <div className={startAdornmentProps.className}>{startAdornment}</div>
      )}
      {children && (
        <span className={labelProps.className} style={labelProps.style}>
          {children}
        </span>
      )}
      {endAdornment && <div className={endAdornmentProps.className}>{endAdornment}</div>}
      {loading && <BrandLoader size='sm' />}
    </>
  );

  const buttonProps = {
    onDoubleClick: onDoubleClick,
    disabled: disabled || loading,
    'data-variant': size === 'clear' || size === 'free' ? 'clear' : variant,
    className: rootProps.className,
    style: {
      ...rootProps.style,
      width: widthFullSize ? '100%' : 'initial',
    },
  };

  if (!to) {
    return (
      <button {...domProps} {...buttonProps} ref={ref} onClick={handleClick}>
        {content}
      </button>
    );
  }

  if (to && nav) {
    return (
      <NavLink {...buttonProps} to={buttonProps.disabled ? '#' : to || '/'}>
        {content}
      </NavLink>
    );
  }

  if (to && !nav) {
    return (
      <Link {...buttonProps} to={buttonProps.disabled ? '#' : to || '/'}>
        {content}
      </Link>
    );
  }
};

export const Button = React.memo(React.forwardRef(ButtonInternal));
